import { createContext, useContext } from 'react';

/**
 * What a toast SAYS about the write that raised it.
 *
 * Two tones and no third, because the two behave differently rather than merely looking
 * different — see `<ToastProvider>`, where the success dismisses itself and the failure does
 * not. A general `tone` prop would let a caller ask for an error that disappears, which is
 * the one combination this component exists to refuse.
 */
export type ToastTone = 'success' | 'failure';

export interface ToastMessage {
  id: number;
  tone: ToastTone;
  message: string;
}

export interface ToastApi {
  /** The write landed. Auto-dismisses. */
  success: (message: string) => void;

  /** The write was refused. Stays until the reader dismisses it. */
  failure: (message: string) => void;
}

export const ToastContext = createContext<ToastApi | null>(null);

/**
 * The one way to raise a toast.
 *
 * ```tsx
 * const toast = useToast();
 * toast.success(`Approved ${request.subjectReference}.`);
 * toast.failure(failure.message);
 * ```
 *
 * **It throws rather than returning a no-op when the provider is missing.** A silent no-op is
 * a confirmation nobody ever sees and nothing anywhere reporting why — which is the exact
 * failure a toast is for. `<ToastProvider>` is mounted above the routes, so the only way to
 * reach this is to render a component outside the app shell.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);

  if (!api) {
    throw new Error('useToast must be called inside <ToastProvider>.');
  }

  return api;
}
